import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  LayoutGrid, RefreshCw, Search, TrendingUp, TrendingDown, 
  Clock, X, ChevronUp, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OptionChainModal = ({ isOpen, onClose, initialSymbol = '' }) => {
  const [symbol, setSymbol] = useState(initialSymbol);
  const [loading, setLoading] = useState(false);
  const [chainData, setChainData] = useState(null);
  const [selectedExpiration, setSelectedExpiration] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [strikesFilter, setStrikesFilter] = useState('30');

  useEffect(() => {
    if (initialSymbol && isOpen) {
      setSymbol(initialSymbol);
      fetchChain(null, initialSymbol);
    }
  }, [initialSymbol, isOpen]);

  const fetchChain = useCallback(async (exp = null, sym = null) => {
    const searchSymbol = sym || symbol;
    if (!searchSymbol.trim()) {
      toast.error('Please enter a stock symbol');
      return;
    }
    
    setLoading(true);
    
    try {
      const expParam = exp || selectedExpiration;
      const url = expParam 
        ? `${API}/data/chain-greeks/${searchSymbol.toUpperCase()}?expiration=${expParam}`
        : `${API}/data/chain-greeks/${searchSymbol.toUpperCase()}`;
      
      const response = await axios.get(url);
      setChainData(response.data);
      
      if (!selectedExpiration && response.data.expiration) {
        setSelectedExpiration(response.data.expiration);
      }
      
      toast.success(`Loaded ${response.data.calls_count} calls, ${response.data.puts_count} puts`);
    } catch (err) {
      console.error('Error fetching chain:', err);
      toast.error(err.response?.data?.detail || 'Failed to fetch option chain');
    } finally {
      setLoading(false);
    }
  }, [symbol, selectedExpiration]);

  const handleExpirationChange = (exp) => {
    setSelectedExpiration(exp);
    fetchChain(exp);
  };

  // Filter strikes around ATM
  const filterStrikes = (options) => {
    if (!options || options.length === 0) return [];
    if (strikesFilter === 'all') return options;
    
    const count = parseInt(strikesFilter);
    const targetPrice = chainData?.current_price;
    
    if (!targetPrice) return options.slice(0, count);
    
    let atmIndex = 0;
    let minDiff = Infinity;
    options.forEach((o, i) => {
      const diff = Math.abs(o.strike - targetPrice);
      if (diff < minDiff) {
        minDiff = diff;
        atmIndex = i;
      }
    });
    
    const halfCount = Math.floor(count / 2);
    const start = Math.max(0, atmIndex - halfCount);
    const end = Math.min(options.length, start + count);
    return options.slice(start, end);
  };

  const formatChange = (change, pctChange) => {
    if (!change && change !== 0) return <span className="text-gray-400">-</span>;
    const color = change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-500';
    const icon = change > 0 ? <ChevronUp className="h-3 w-3" /> : change < 0 ? <ChevronDown className="h-3 w-3" /> : null;
    return (
      <span className={`flex items-center ${color}`}>
        {icon}
        {Math.abs(change).toFixed(2)}
        {pctChange !== undefined && <span className="ml-1 text-xs">({pctChange > 0 ? '+' : ''}{pctChange.toFixed(1)}%)</span>}
      </span>
    );
  };

  const isATM = (strike) => {
    if (!chainData?.current_price) return false;
    const filteredCalls = filterStrikes(chainData.calls || []);
    let minDiff = Infinity;
    let atmStrike = null;
    filteredCalls.forEach(o => {
      const diff = Math.abs(o.strike - chainData.current_price);
      if (diff < minDiff) {
        minDiff = diff;
        atmStrike = o.strike;
      }
    });
    return strike === atmStrike;
  };

  const OptionDetailCard = ({ option, type }) => {
    if (!option) return null;
    
    return (
      <div className="bg-white rounded-lg border p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Badge className={type === 'call' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
              {type === 'call' ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
              {type.toUpperCase()}
            </Badge>
            <span className="font-bold text-lg">${option.strike}</span>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-indigo-600">${option.mark || option.last || '-'}</div>
            <div className="text-xs text-gray-500">Mark Price</div>
          </div>
        </div>

        {/* Key Stats Row */}
        <div className="grid grid-cols-4 gap-3 py-3 border-y">
          <div>
            <div className="text-xs text-gray-500">Breakeven</div>
            <div className="font-semibold">${option.breakeven}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Chance of Profit</div>
            <div className="font-semibold text-green-600">{option.prob_otm}%</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">% Change</div>
            <div className={`font-semibold ${option.pct_change > 0 ? 'text-green-600' : option.pct_change < 0 ? 'text-red-600' : ''}`}>
              {option.pct_change > 0 ? '+' : ''}{option.pct_change}%
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Change</div>
            <div className={`font-semibold ${option.change > 0 ? 'text-green-600' : option.change < 0 ? 'text-red-600' : ''}`}>
              {option.change > 0 ? '+' : ''}${option.change}
            </div>
          </div>
        </div>

        {/* Bid/Ask Section */}
        <div className="grid grid-cols-2 gap-4 py-3 border-b">
          <div>
            <div className="text-xs text-gray-500">Bid</div>
            <div className="font-medium">${option.bid || '-'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Ask</div>
            <div className="font-medium">${option.ask || '-'}</div>
          </div>
        </div>

        {/* Volume & OI */}
        <div className="grid grid-cols-3 gap-3 py-3 border-b">
          <div>
            <div className="text-xs text-gray-500">Last Trade</div>
            <div className="font-medium">${option.last || '-'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Volume</div>
            <div className="font-medium">{option.volume?.toLocaleString() || '-'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Open Interest</div>
            <div className="font-medium">{option.openInterest?.toLocaleString() || '-'}</div>
          </div>
        </div>

        {/* IV */}
        <div className="py-3 border-b">
          <div className="text-xs text-gray-500">Implied Volatility</div>
          <div className="font-semibold text-purple-600">{option.iv}%</div>
        </div>

        {/* The Greeks */}
        <div className="pt-3">
          <div className="text-sm font-semibold text-gray-700 mb-2">The Greeks</div>
          <div className="grid grid-cols-5 gap-2 text-center">
            <div>
              <div className="text-xs text-gray-500">Delta</div>
              <div className="font-mono text-sm">{option.delta}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Gamma</div>
              <div className="font-mono text-sm">{option.gamma}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Theta</div>
              <div className="font-mono text-sm">{option.theta}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Vega</div>
              <div className="font-mono text-sm">{option.vega}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Rho</div>
              <div className="font-mono text-sm">{option.rho}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Side-by-side table row
  const SideBySideRow = ({ call, put, strike }) => {
    const atmHighlight = isATM(strike);
    
    return (
      <div 
        className={`flex items-center py-2 px-2 text-xs border-b hover:bg-blue-50 cursor-pointer transition-colors
          ${atmHighlight ? 'bg-yellow-50 border-yellow-200' : ''}
        `}
        onClick={() => setSelectedOption({ call, put, strike })}
        data-testid={`chain-row-${strike}`}
      >
        {/* CALLS side - 6 columns */}
        <div className={`w-12 text-right ${call?.itm ? 'bg-green-50' : ''}`}>{call?.openInterest?.toLocaleString() || '-'}</div>
        <div className={`w-12 text-right ${call?.itm ? 'bg-green-50' : ''}`}>{call?.volume?.toLocaleString() || '-'}</div>
        <div className={`w-14 text-right text-purple-600 ${call?.itm ? 'bg-green-50' : ''}`}>{call?.iv || '-'}%</div>
        <div className={`w-14 text-right text-blue-600 ${call?.itm ? 'bg-green-50' : ''}`}>{call?.delta || '-'}</div>
        <div className={`w-14 text-right ${call?.itm ? 'bg-green-50' : ''}`}>{call?.bid || '-'}</div>
        <div className={`w-14 text-right font-medium ${call?.itm ? 'bg-green-50' : ''}`}>{call?.ask || '-'}</div>
        
        {/* STRIKE (center) */}
        <div className={`w-20 text-center font-bold mx-2 py-1 rounded ${atmHighlight ? 'text-yellow-700 bg-yellow-100' : 'bg-gray-100'}`}>
          {strike}
          {atmHighlight && <span className="block text-[9px]">ATM</span>}
        </div>
        
        {/* PUTS side - 6 columns */}
        <div className={`w-14 text-left font-medium ${put?.itm ? 'bg-red-50' : ''}`}>{put?.bid || '-'}</div>
        <div className={`w-14 text-left ${put?.itm ? 'bg-red-50' : ''}`}>{put?.ask || '-'}</div>
        <div className={`w-14 text-left text-blue-600 ${put?.itm ? 'bg-red-50' : ''}`}>{put?.delta || '-'}</div>
        <div className={`w-14 text-left text-purple-600 ${put?.itm ? 'bg-red-50' : ''}`}>{put?.iv || '-'}%</div>
        <div className={`w-12 text-left ${put?.itm ? 'bg-red-50' : ''}`}>{put?.volume?.toLocaleString() || '-'}</div>
        <div className={`w-12 text-left ${put?.itm ? 'bg-red-50' : ''}`}>{put?.openInterest?.toLocaleString() || '-'}</div>
      </div>
    );
  };

  // Build combined data by strike
  const getCombinedData = () => {
    if (!chainData) return [];
    
    const callsFiltered = filterStrikes(chainData.calls || []);
    const putsFiltered = filterStrikes(chainData.puts || []);
    
    const callsMap = {};
    const putsMap = {};
    
    callsFiltered.forEach(c => callsMap[c.strike] = c);
    putsFiltered.forEach(p => putsMap[p.strike] = p);
    
    const allStrikes = [...new Set([
      ...callsFiltered.map(c => c.strike),
      ...putsFiltered.map(p => p.strike)
    ])].sort((a, b) => a - b);
    
    return allStrikes.map(strike => ({
      strike,
      call: callsMap[strike],
      put: putsMap[strike]
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl w-[95vw] h-[90vh] p-0 overflow-hidden">
        <DialogHeader className="p-4 border-b bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center text-xl">
              <LayoutGrid className="h-6 w-6 mr-2" />
              Option Chain
            </DialogTitle>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="h-5 w-5" />
            </Button>
          </div>
          <DialogDescription className="text-indigo-100">
            Full option chain with Greeks • Click any row for details
          </DialogDescription>
        </DialogHeader>

        <div className="p-4 flex flex-col h-[calc(90vh-80px)]">
          {/* Search & Controls */}
          <div className="flex items-center space-x-3 mb-4">
            <Input
              placeholder="Symbol (e.g., SPY, AAPL)"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              onKeyPress={(e) => e.key === 'Enter' && fetchChain()}
              className="w-40"
              data-testid="modal-chain-symbol"
            />
            <Button 
              onClick={() => fetchChain()} 
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="modal-chain-search"
            >
              {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>

            {chainData && (
              <>
                <Select value={selectedExpiration} onValueChange={handleExpirationChange}>
                  <SelectTrigger className="w-44" data-testid="modal-expiration">
                    <SelectValue placeholder="Expiration" />
                  </SelectTrigger>
                  <SelectContent>
                    {chainData.expirations?.map((exp) => (
                      <SelectItem key={exp} value={exp}>{exp}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={strikesFilter} onValueChange={setStrikesFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20 Strikes</SelectItem>
                    <SelectItem value="30">30 Strikes</SelectItem>
                    <SelectItem value="50">50 Strikes</SelectItem>
                    <SelectItem value="all">All Strikes</SelectItem>
                  </SelectContent>
                </Select>

                <div className="flex items-center space-x-3 ml-auto">
                  <Badge variant="outline" className="text-lg px-3 py-1">
                    {chainData.symbol} <span className="text-green-600 ml-2">${chainData.current_price}</span>
                  </Badge>
                  <Badge variant="secondary">
                    <Clock className="h-3 w-3 mr-1" />
                    {chainData.days_to_exp} DTE
                  </Badge>
                </div>
              </>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 flex gap-4 overflow-hidden">
            {/* Side-by-Side Chain Table */}
            <div className="flex-1 border rounded-lg overflow-hidden">
              {/* Table Header */}
              <div className="flex items-center py-2 px-2 bg-gray-100 text-xs font-semibold sticky top-0 z-10">
                {/* CALLS header */}
                <div className="w-12 text-right text-green-700">OI</div>
                <div className="w-12 text-right text-green-700">Vol</div>
                <div className="w-14 text-right text-green-700">IV</div>
                <div className="w-14 text-right text-green-700">Δ</div>
                <div className="w-14 text-right text-green-700">Bid</div>
                <div className="w-14 text-right text-green-700">Ask</div>
                
                {/* Strike header */}
                <div className="w-20 text-center bg-gray-200 font-bold mx-2 py-1 rounded">Strike</div>
                
                {/* PUTS header */}
                <div className="w-14 text-left text-red-700">Bid</div>
                <div className="w-14 text-left text-red-700">Ask</div>
                <div className="w-14 text-left text-red-700">Δ</div>
                <div className="w-14 text-left text-red-700">IV</div>
                <div className="w-12 text-left text-red-700">Vol</div>
                <div className="w-12 text-left text-red-700">OI</div>
              </div>

              {/* Labels row */}
              <div className="flex items-center py-1 px-2 bg-gray-50 text-[10px] text-gray-500">
                <div className="flex-1 text-center text-green-600 font-semibold">
                  ← CALLS ({chainData?.calls_count || 0})
                </div>
                <div className="w-20 mx-2"></div>
                <div className="flex-1 text-center text-red-600 font-semibold">
                  PUTS ({chainData?.puts_count || 0}) →
                </div>
              </div>

              {/* Table Body */}
              <ScrollArea className="h-[calc(100%-80px)]">
                {getCombinedData().map(({ strike, call, put }) => (
                  <SideBySideRow key={strike} strike={strike} call={call} put={put} />
                ))}
                {(!chainData || getCombinedData().length === 0) && (
                  <div className="text-center py-12 text-gray-500">
                    <LayoutGrid className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>Enter a symbol to load the option chain</p>
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Detail Panel */}
            {selectedOption && (
              <div className="w-80 flex-shrink-0 space-y-4 overflow-y-auto">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">Strike ${selectedOption.strike}</h3>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedOption(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                {selectedOption.call && (
                  <OptionDetailCard option={selectedOption.call} type="call" />
                )}
                
                {selectedOption.put && (
                  <OptionDetailCard option={selectedOption.put} type="put" />
                )}
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center space-x-6 pt-3 border-t text-xs text-gray-500">
            <span className="flex items-center"><div className="w-3 h-3 bg-green-50 border mr-1"></div> ITM Call</span>
            <span className="flex items-center"><div className="w-3 h-3 bg-red-50 border mr-1"></div> ITM Put</span>
            <span className="flex items-center"><div className="w-3 h-3 bg-yellow-100 border border-yellow-300 mr-1"></div> ATM</span>
            <span>Δ = Delta • IV = Implied Volatility • OI = Open Interest • Vol = Volume</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OptionChainModal;
